import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { CiteThis } from "@/components/site/CiteThis";
import { GBP_CATEGORIES, getCategoryBySlug } from "@/lib/reference/categories";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/tools/gbp-categories/$slug")({
  loader: ({ params }) => {
    const c = getCategoryBySlug(params.slug);
    if (!c) throw notFound();
    return c;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const url = `${SITE.url}/tools/gbp-categories/${params.slug}`;
    const title = `${loaderData.name} — GBP Category`;
    const desc = loaderData.description.slice(0, 158);
    return {
      meta: [
        { title: `${title} | ${SITE.name}` },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTerm",
            name: loaderData.name,
            alternateName: loaderData.synonyms,
            description: loaderData.description,
            url,
            inDefinedTermSet: `${SITE.url}/tools/gbp-categories`,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE.url}/tools` },
              { "@type": "ListItem", position: 3, name: "GBP Categories", item: `${SITE.url}/tools/gbp-categories` },
              { "@type": "ListItem", position: 4, name: loaderData.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: CategoryDetail,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-5 sm:px-8 py-24 text-center">
        <p className="eyebrow">Not found</p>
        <h1 className="headline-display mt-3 text-4xl">Category not indexed yet</h1>
        <Link to="/tools/gbp-categories" className="mt-6 inline-block underline">Back to category finder</Link>
      </main>
      <SiteFooter />
    </div>
  ),
});

function CategoryDetail() {
  const params = Route.useParams();
  const c = Route.useLoaderData();
  const url = `${SITE.url}/tools/gbp-categories/${params.slug}`;
  const related = (c.related as string[])
    .map((slug: string) => GBP_CATEGORIES.find((x) => x.slug === slug))
    .filter((x): x is NonNullable<typeof x> => Boolean(x));

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <article className="mx-auto max-w-3xl px-5 sm:px-8 py-16">
        <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
          <ol className="flex items-center gap-2 flex-wrap">
            <li><Link to="/" className="hover:text-accent">Home</Link></li>
            <li aria-hidden>/</li>
            <li><Link to="/tools" className="hover:text-accent">Tools</Link></li>
            <li aria-hidden>/</li>
            <li><Link to="/tools/gbp-categories" className="hover:text-accent">GBP Categories</Link></li>
            <li aria-hidden>/</li>
            <li>{c.name}</li>
          </ol>
        </nav>
        <header className="mt-6">
          <p className="eyebrow">{c.industry}</p>
          <h1 className="headline-display mt-3 text-5xl">{c.name}</h1>
          {c.synonyms.length > 0 && (
            <p className="mt-2 text-sm uppercase tracking-wider text-muted-foreground">
              Also known as: {c.synonyms.join(", ")}
            </p>
          )}
        </header>

        <p className="mt-8 text-xl leading-relaxed font-serif text-foreground/90">{c.description}</p>

        <section className="mt-10 grid gap-6 sm:grid-cols-2">
          <div className="rounded-sm border border-border bg-paper-soft p-5">
            <p className="eyebrow">Best for</p>
            <p className="mt-2 text-sm leading-relaxed">{c.bestFor}</p>
          </div>
          <div className="rounded-sm border border-border bg-paper-soft p-5">
            <p className="eyebrow">Availability</p>
            <p className="mt-2 text-sm leading-relaxed">
              {c.availability === "global" ? "Available globally on Google Business Profile." : `Limited to: ${c.availability.join(", ")}`}
            </p>
          </div>
        </section>

        {c.pitfall && (
          <aside className="mt-8 rounded-sm border-l-4 border-accent bg-accent/5 px-5 py-4">
            <p className="eyebrow text-accent">Common pitfall</p>
            <p className="mt-1 text-sm leading-relaxed">{c.pitfall}</p>
          </aside>
        )}

        <section className="mt-10">
          <h2 className="text-2xl font-semibold">Attributes typically unlocked</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {c.unlocksAttributes.map((a: string) => (
              <li key={a} className="rounded-sm border border-border bg-background px-3 py-2 text-sm">
                {a}
              </li>
            ))}
          </ul>
        </section>

        {related.length > 0 && (
          <section className="mt-10">
            <h2 className="text-2xl font-semibold">Often confused with</h2>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {related.map((r: { slug: string; name: string }) => (
                <li key={r.slug}>
                  <Link
                    to="/tools/gbp-categories/$slug"
                    params={{ slug: r.slug }}
                    className="block rounded-sm border border-border bg-paper-soft px-3 py-2 text-sm hover:border-foreground transition-colors"
                  >
                    {r.name}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-12 flex items-center gap-3 border-t border-b border-border py-5">
          <CiteThis title={`${c.name} — GBP Category`} url={url} author={`${SITE.name} Newsroom`} />
          <p className="text-xs text-muted-foreground">Free to cite with a link back.</p>
        </div>

        <NewsletterCTA />
      </article>
      <SiteFooter />
    </div>
  );
}
